import React, { useState, useEffect } from 'react';
import { api } from '../../api';
import { colors, fontFamily } from '../../theme';

const card = { background: colors.cardBg, boxShadow: colors.shadowCard, border: `1px solid ${colors.goldMuted}`, borderRadius: 8, padding: '1.25rem', marginBottom: '1.25rem' };
const btnPrimary = { padding: '0.45rem 0.9rem', background: colors.goldActiveBg, border: `1px solid ${colors.gold}`, color: colors.textPrimary, borderRadius: 4, cursor: 'pointer', fontFamily, fontSize: '0.82rem' };
const btnDanger = { padding: '0.45rem 0.9rem', background: 'rgba(220,53,69,0.2)', border: `1px solid ${colors.negative}`, color: colors.negative, borderRadius: 4, cursor: 'pointer', fontFamily, fontSize: '0.82rem' };
const inputStyle = { padding: '0.5rem 0.7rem', background: colors.sectionBg, border: `1px solid ${colors.goldMuted}`, borderRadius: 4, color: colors.textPrimary, fontFamily, fontSize: '0.82rem' };

export default function LiveFraudManagement() {
  const [flagged, setFlagged] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [incidentsLoading, setIncidentsLoading] = useState(false);
  const [actionInput, setActionInput] = useState('');
  const [filter, setFilter] = useState('unreviewed');

  useEffect(() => { loadFlagged(); }, []);

  const loadFlagged = async () => {
    try {
      setLoading(true);
      const data = await api.admin.getFraudFlagged();
      setFlagged(data.flagged || []);
      setError(null);
    } catch (err) {
      setError('Failed to load fraud flags');
    } finally {
      setLoading(false);
    }
  };

  const openIncidents = async (flag) => {
    setSelectedFlag(flag);
    setIncidents([]);
    setActionInput('');
    setIncidentsLoading(true);
    try {
      const data = await api.admin.getFraudIncidents(flag.user_id);
      setIncidents(data.incidents || []);
    } catch {}
    finally { setIncidentsLoading(false); }
  };

  const submitReview = async (flagId) => {
    try {
      await api.admin.reviewFraudFlag(flagId, actionInput);
      setSelectedFlag(s => s ? { ...s, reviewed: true, action_taken: actionInput } : s);
      loadFlagged();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to submit review');
    }
  };

  const filtered = flagged.filter(f => {
    if (filter === 'unreviewed') return !f.reviewed;
    if (filter === 'reviewed') return f.reviewed;
    return true;
  });

  const unreviewedCount = flagged.filter(f => !f.reviewed).length;

  const filterBtn = (val, label) => (
    <button
      onClick={() => setFilter(val)}
      style={{ padding: '0.3rem 0.75rem', fontSize: '0.78rem', borderRadius: 20, border: 'none', cursor: 'pointer', fontFamily, background: filter === val ? colors.gold : 'rgba(255,255,255,0.1)', color: filter === val ? '#1a0a1f' : colors.textMuted }}
    >
      {label}
    </button>
  );

  if (loading) return <div style={{ color: colors.textMuted, padding: '3rem', textAlign: 'center' }}>Loading...</div>;
  if (error) return <div style={{ color: colors.negative, padding: '1rem', background: 'rgba(220,53,69,0.1)', borderRadius: 6 }}>{error}</div>;

  return (
    <div>
      {/* Header */}
      <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ color: colors.textPrimary, margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>Live Odds Fraud Detection</h2>
          <p style={{ color: colors.textMuted, margin: '0.3rem 0 0 0', fontSize: '0.82rem' }}>
            Accounts flagged for repeatedly placing bets just before significant odds drops.
            Requires manual review before any action is taken.
          </p>
        </div>
        <button onClick={loadFlagged} style={btnPrimary}>Refresh</button>
      </div>

      {/* How it works */}
      <div style={{ ...card, background: 'rgba(191,149,63,0.07)', border: `1px solid ${colors.goldMuted}` }}>
        <h3 style={{ color: colors.textPrimary, margin: '0 0 0.5rem 0', fontSize: '0.9rem' }}>How detection works</h3>
        <ul style={{ color: colors.textMuted, fontSize: '0.82rem', margin: 0, paddingLeft: '1.2rem', lineHeight: 1.8 }}>
          <li>The live odds WebSocket feed tracks every odds change ≥ 0.10 decimal per market selection.</li>
          <li>After each change, the system looks back 30 seconds for bets placed on that selection.</li>
          <li>If a bet was placed at higher odds than the post-change price, it is recorded as a <em>suspicious incident</em>.</li>
          <li>An account is flagged once it accumulates 3 or more incidents within a 7-day rolling window.</li>
          <li>All flags require manual admin review — no automatic account actions are taken.</li>
        </ul>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
        {[
          { label: 'Total Flagged', value: flagged.length },
          { label: 'Unreviewed', value: unreviewedCount, highlight: unreviewedCount > 0 },
          { label: 'Reviewed', value: flagged.length - unreviewedCount },
        ].map(s => (
          <div key={s.label} style={{ ...card, textAlign: 'center', marginBottom: 0, ...(s.highlight ? { background: 'rgba(220,53,69,0.1)', border: '1px solid rgba(220,53,69,0.3)' } : {}) }}>
            <p style={{ color: colors.textMuted, fontSize: '0.75rem', margin: '0 0 0.3rem 0' }}>{s.label}</p>
            <p style={{ color: s.highlight ? colors.negative : colors.textPrimary, fontSize: '1.8rem', fontWeight: 700, margin: 0 }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {filterBtn('unreviewed', `Unreviewed (${unreviewedCount})`)}
        {filterBtn('reviewed', `Reviewed (${flagged.length - unreviewedCount})`)}
        {filterBtn('all', `All (${flagged.length})`)}
      </div>

      {/* Flagged list */}
      {filtered.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', color: colors.textMuted, padding: '2.5rem' }}>
          {filter === 'unreviewed' ? 'No unreviewed flags — all clear.' : 'No flags in this category.'}
        </div>
      ) : (
        <div style={card}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.goldMuted}` }}>
                {['User', 'Incidents (7d)', 'Flagged At', 'Status', 'Action Taken', 'Actions'].map(h => (
                  <th key={h} style={{ color: colors.textMuted, padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 'normal' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(flag => (
                <tr key={flag.id} style={{ borderBottom: `1px solid ${colors.sectionBg}`, background: selectedFlag?.id === flag.id ? 'rgba(191,149,63,0.06)' : 'transparent' }}>
                  <td style={{ padding: '0.55rem 0.75rem' }}>
                    <div style={{ color: colors.textPrimary, fontWeight: 600 }}>{flag.user_name}</div>
                    <div style={{ color: colors.textMuted, fontSize: '0.75rem' }}>@{flag.user_username}</div>
                  </td>
                  <td style={{ padding: '0.55rem 0.75rem' }}>
                    <span style={{ color: colors.negative, fontWeight: 700, fontSize: '1rem' }}>{flag.incident_count}</span>
                  </td>
                  <td style={{ padding: '0.55rem 0.75rem', color: colors.textMuted, fontSize: '0.78rem' }}>
                    {flag.flagged_at ? new Date(flag.flagged_at).toLocaleString() : '—'}
                  </td>
                  <td style={{ padding: '0.55rem 0.75rem' }}>
                    <span style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: 20, background: flag.reviewed ? `${colors.odds}26` : 'rgba(220,53,69,0.15)', color: flag.reviewed ? colors.odds : colors.negative }}>
                      {flag.reviewed ? 'Reviewed' : 'Pending'}
                    </span>
                  </td>
                  <td style={{ padding: '0.55rem 0.75rem', color: colors.textMuted, fontSize: '0.78rem' }}>
                    {flag.action_taken || '—'}
                  </td>
                  <td style={{ padding: '0.55rem 0.75rem' }}>
                    <button onClick={() => openIncidents(flag)} style={btnPrimary}>View Incidents</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Incident detail panel */}
      {selectedFlag && (
        <div style={{ ...card, border: `1px solid ${colors.cardBgActive}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
            <div>
              <h3 style={{ color: colors.textPrimary, margin: 0, fontSize: '1rem' }}>
                Suspicious Incidents — {selectedFlag.user_name}
              </h3>
              <p style={{ color: colors.textMuted, margin: '0.2rem 0 0 0', fontSize: '0.8rem' }}>
                @{selectedFlag.user_username} · {selectedFlag.incident_count} incidents in 7 days
              </p>
            </div>
            <button onClick={() => setSelectedFlag(null)} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', fontSize: '1.2rem' }}>×</button>
          </div>

          {incidentsLoading ? (
            <p style={{ color: colors.textMuted }}>Loading incidents…</p>
          ) : incidents.length === 0 ? (
            <p style={{ color: colors.textMuted }}>No incidents found.</p>
          ) : (
            <div style={{ overflowX: 'auto', marginBottom: '1.25rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${colors.goldMuted}` }}>
                    {['Bet Placed At', 'Selection', 'Market', 'Bet Odds', 'Post-Change Odds', 'Advantage', 'Secs Before Change', 'Bet Amount'].map(h => (
                      <th key={h} style={{ color: colors.textMuted, padding: '0.4rem 0.6rem', textAlign: 'left', fontWeight: 'normal', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {incidents.map(inc => (
                    <tr key={inc.id} style={{ borderBottom: `1px solid ${colors.sectionBg}` }}>
                      <td style={{ padding: '0.4rem 0.6rem', color: colors.textMuted, fontSize: '0.77rem', whiteSpace: 'nowrap' }}>
                        {inc.bet_placed_at ? new Date(inc.bet_placed_at).toLocaleString() : '—'}
                      </td>
                      <td style={{ padding: '0.4rem 0.6rem', color: colors.textPrimary }}>{inc.selection || '—'}</td>
                      <td style={{ padding: '0.4rem 0.6rem', color: colors.textMuted }}>{inc.market_type || '—'}</td>
                      <td style={{ padding: '0.4rem 0.6rem', color: colors.textPrimary, fontFamily: 'monospace' }}>{inc.bet_odds?.toFixed(3)}</td>
                      <td style={{ padding: '0.4rem 0.6rem', color: colors.negative, fontFamily: 'monospace' }}>{inc.odds_after_change?.toFixed(3)}</td>
                      <td style={{ padding: '0.4rem 0.6rem', color: colors.odds, fontFamily: 'monospace' }}>+{inc.odds_advantage?.toFixed(3)}</td>
                      <td style={{ padding: '0.4rem 0.6rem', color: colors.pending, fontFamily: 'monospace' }}>{inc.seconds_before_change?.toFixed(1)}s</td>
                      <td style={{ padding: '0.4rem 0.6rem', color: colors.textPrimary }}>{inc.bet_amount != null ? `${inc.bet_amount.toFixed(2)}₵₵` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Review form */}
          {!selectedFlag.reviewed && (
            <div style={{ borderTop: `1px solid ${colors.goldMuted}`, paddingTop: '1rem', display: 'flex', gap: '0.75rem', alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', color: colors.textMuted, fontSize: '0.8rem', marginBottom: '0.3rem' }}>
                  Action taken (e.g. "Account suspended", "Warning issued", "No action — false positive")
                </label>
                <input
                  type="text"
                  value={actionInput}
                  onChange={e => setActionInput(e.target.value)}
                  placeholder="Describe action taken…"
                  style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                />
              </div>
              <button onClick={() => submitReview(selectedFlag.id)} style={btnDanger}>
                Mark Reviewed
              </button>
            </div>
          )}
          {selectedFlag.reviewed && (
            <div style={{ borderTop: `1px solid ${colors.goldMuted}`, paddingTop: '0.75rem' }}>
              <span style={{ color: colors.odds, fontSize: '0.85rem' }}>
                ✓ Reviewed by {selectedFlag.reviewed_by || 'admin'} — {selectedFlag.action_taken || 'no action recorded'}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
